/**
 * @file ViewLivePage.js
 * @description View Live page for Client Admins to experience portal pages as
 * Individual users see them. Loads all portal pages without population group
 * scoping and renders them with navigable tabs and the freeform grid layout.
 */
import { useState, useEffect, useCallback } from 'react';
import './HomePage.css';
import { useAuthContext } from '../../store/Contexts';
import { useTheme } from '../../store/ThemeContext';
import { Avatar } from '@mui/material';
import {
    queryPortalPages,
    readPortalPage,
} from '../../services/portalPageService';
import Spinner from '../ui/spinners/Spinner';
import styles from './HomePage.module.scss';
import viewLiveStyles from './ViewLivePage.module.scss';
import FreeformGrid from '../fluid-grid/FreeformGrid';
import ViewLiveBanner from './ViewLiveBanner';
import { ROUTE_BUILDERS } from '../../constants/routes';
import usePortalTabs from '../../hooks/usePortalTabs';
import usePortalPageLoader from '../../hooks/usePortalPageLoader';
import PortalTabBar from '../shared/PortalTabBar';
import { TenantTiers } from '../../utils/data/enums';
import { getText } from '../../i18n';

function ViewLivePage() {
    const {
        isLoggedIn,
        user,
        menuLabels,
        displayIndividualAssetMenu,
        clientTierMeetsRequirement,
    } = useAuthContext();
    const themeStyles = useTheme();
    const [viewMode, setViewMode] = useState('desktop');

    const fetchPortalPagesFn = useCallback(async () => {
        const result = await queryPortalPages(false);
        return {
            viewModels: result.viewModels.filter(p => p.status === 1),
        };
    }, []);

    const {
        isLoading,
        setIsLoading,
        tabs,
        selectedTabId,
        portalId,
        visibleTabs,
        showLeftArrow,
        showRightArrow,
        scroll,
        openTab,
        fetchTabs,
        handleMouseDown,
        handleMouseLeave,
        handleMouseUp,
        handleMouseMove,
    } = usePortalTabs({
        fetchPortalPagesFn,
        routeBuilder: ROUTE_BUILDERS.viewLivePortal,
    });

    const loadFn = useCallback(async id => readPortalPage(id, true), []);

    const {
        fetchedContainers,
        fetchedMobileConfiguration,
        portalName,
        isPageLoading,
        loadPortalPage,
    } = usePortalPageLoader(loadFn);

    useEffect(() => {
        fetchTabs();
    }, []);

    useEffect(() => {
        if (selectedTabId !== null) {
            setIsLoading(true);
            loadPortalPage(selectedTabId).finally(() => setIsLoading(false));
        }
    }, [selectedTabId]);

    const isTabLoading = isPageLoading;

    const mobileTabLinks = tabs.map(tab => {
        const isActive = tab.id === selectedTabId;
        return (
            <div
                key={tab.id}
                className={`${viewLiveStyles.mobileTabPill} ${isActive ? '' : viewLiveStyles.mobileTabPillInactive}`}
                style={
                    isActive
                        ? {
                              background:
                                  themeStyles?.accessoriesBg1 || '#1890ff',
                              border: `1px solid ${themeStyles?.accessoriesBg1 || '#1890ff'}`,
                          }
                        : undefined
                }
            >
                <button
                    onClick={() => openTab(tab)}
                    className={`${viewLiveStyles.mobileTabButton} ${isActive ? '' : viewLiveStyles.mobileTabButtonInactive}`}
                    style={
                        isActive
                            ? { color: themeStyles?.accessoriesFg1 || 'white' }
                            : undefined
                    }
                >
                    {tab.name}
                </button>
            </div>
        );
    });

    const freeformGridContent = (
        <FreeformGrid
            fetchedContainers={fetchedContainers}
            fetchedMobileConfiguration={fetchedMobileConfiguration}
            navigationStack={[portalId]}
            portalName={portalName}
            portalId={portalId}
            isViewLive={true}
            previewMode={viewMode}
        />
    );

    return (
        isLoggedIn && (
            <>
                {isLoading ? (
                    <div className="tabLoadingIndicator">
                        <Spinner className={styles.loadingSpinner} />
                    </div>
                ) : (
                    <div className={viewLiveStyles.pageContainer}>
                        {viewMode !== 'mobile' && (
                            <PortalTabBar
                                visibleTabs={visibleTabs}
                                selectedTabId={selectedTabId}
                                showLeftArrow={showLeftArrow}
                                showRightArrow={showRightArrow}
                                scroll={scroll}
                                openTab={openTab}
                                handleMouseDown={handleMouseDown}
                                handleMouseLeave={handleMouseLeave}
                                handleMouseUp={handleMouseUp}
                                handleMouseMove={handleMouseMove}
                            />
                        )}

                        {isTabLoading && viewMode !== 'mobile' ? (
                            <div className="tabLoadingIndicator">
                                <Spinner className={styles.loadingSpinner} />
                            </div>
                        ) : null}
                        <div
                            className={`${viewLiveStyles.contentWrapper} ${isTabLoading ? viewLiveStyles.contentLoading : ''}`}
                        >
                            <div
                                className={
                                    viewMode === 'mobile'
                                        ? viewLiveStyles.frameMobile
                                        : viewLiveStyles.frameDesktop
                                }
                            >
                                {viewMode === 'mobile' && (
                                    <div
                                        className={viewLiveStyles.mobileHeader}
                                    >
                                        <span
                                            className={
                                                viewLiveStyles.mobileGreeting
                                            }
                                        >
                                            {`Hello, ${user.preferred_name || user.first_name}!`}
                                        </span>
                                        <Avatar
                                            sx={{
                                                width: 36,
                                                height: 36,
                                                fontSize: '14px',
                                                cursor: 'default',
                                            }}
                                            src={user.avatar_url ?? ''}
                                        >
                                            {user.first_name
                                                ? `${user.first_name[0]}${user.last_name[0]}`
                                                : ''}
                                        </Avatar>
                                    </div>
                                )}
                                {viewMode === 'mobile' ? (
                                    <div
                                        className={
                                            viewLiveStyles.mobileScrollArea
                                        }
                                    >
                                        <div
                                            className={
                                                viewLiveStyles.mobileTabsRow
                                            }
                                        >
                                            {mobileTabLinks}
                                        </div>
                                        {freeformGridContent}
                                    </div>
                                ) : (
                                    freeformGridContent
                                )}
                                {viewMode === 'mobile' && (
                                    <div
                                        className={viewLiveStyles.mobileFooter}
                                    >
                                        <div
                                            className={
                                                viewLiveStyles.footerItem
                                            }
                                        >
                                            <i
                                                className={`bi bi-house-door-fill ${viewLiveStyles.footerIcon} ${viewLiveStyles.footerIconHome}`}
                                            />
                                            <span
                                                className={`${viewLiveStyles.footerLabel} ${viewLiveStyles.footerLabelActive}`}
                                            >
                                                {getText('msg_70f8bb9a8a53')}
                                            </span>
                                        </div>
                                        {displayIndividualAssetMenu && (
                                            <div
                                                className={
                                                    viewLiveStyles.footerItem
                                                }
                                            >
                                                <i
                                                    className={`bi bi-book ${viewLiveStyles.footerIcon} ${viewLiveStyles.footerIconMuted}`}
                                                />
                                                <span
                                                    className={
                                                        viewLiveStyles.footerLabel
                                                    }
                                                >
                                                    {menuLabels.assets}
                                                </span>
                                            </div>
                                        )}
                                        {clientTierMeetsRequirement(
                                            TenantTiers.Bronze,
                                        ) && (
                                            <div
                                                className={
                                                    viewLiveStyles.footerItem
                                                }
                                            >
                                                <i
                                                    className={`bi bi-heart-pulse ${viewLiveStyles.footerIcon} ${viewLiveStyles.footerIconMuted}`}
                                                />
                                                <span
                                                    className={
                                                        viewLiveStyles.footerLabel
                                                    }
                                                >
                                                    {menuLabels.benefits}
                                                </span>
                                            </div>
                                        )}
                                        <div
                                            className={
                                                viewLiveStyles.footerItem
                                            }
                                        >
                                            <i
                                                className={`bi bi-envelope ${viewLiveStyles.footerIcon} ${viewLiveStyles.footerIconMuted}`}
                                            />
                                            <span
                                                className={
                                                    viewLiveStyles.footerLabel
                                                }
                                            >
                                                {menuLabels.messages}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {viewMode !== 'mobile' && (
                                <div
                                    className={
                                        viewLiveStyles.desktopTabBarSpacer
                                    }
                                />
                            )}
                            <ViewLiveBanner
                                viewMode={viewMode}
                                onViewModeChange={setViewMode}
                            />
                        </div>
                    </div>
                )}
            </>
        )
    );
}

export default ViewLivePage;
